import {
  AfterViewInit,
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { DragulaService } from 'ng2-dragula';

import { DatatableComponent } from '@swimlane/ngx-datatable';

import { NgxDataTableConfig } from './ngx-datatable-config';
import { SortEvent } from '../../sort/sort-event';
import { TableConfig } from './table-config';
import { TableBase } from '../table-base';
import { TableEvent } from '../table-event';

import { clone, cloneDeep, defaults, isEqual } from 'lodash';

/**
 * Table component.
 *
 * In order to use drag and drop, please include the following CSS file from ng2-dragula. For example:
 * <code>import 'dragula/dist/dragula.css';</code>
 *
 * For ngx-datatable options, see: https://swimlane.gitbooks.io/ngx-datatable/
 *
 * Note: The underlying ngx-datatable uses ContentChildren to retrieve DataTableColumnDirective (ngx-datatable-column)
 * tags. As a result of wrapping ngx-datatable, these objects are no longer direct descendents and ContentChildren
 * cannot retrieve them. A fix to ContentChildren may be in the works...
 *
 * Instead of using ngx-datatable-column, table cells may be defined using templates, provided as the
 * columns cellTemplate property. For example:
 *
 * <code>
 * this.columns = [{
 *   cellTemplate: this.nameTemplate,
 *   prop: 'name',
 *   name: 'Name'
 * }]
 * </code>
 *
 * and
 *
 * <code>
 * &lt;ng-template #nameTemplate let-row="row"&gt;
 *   &lt;span>{{row.name}}&lt;/span&gt;
 * &lt;/ng-template&gt;
 * </code>
 *
 * Usage:
 * <code><pre>
 * // Individual module import
 * import { TableModule } from 'patternfly-ng/table';
 * // Or
 * import { TableModule } from 'patternfly-ng';
 *
 * // NGX Bootstrap
 * import { BsDropdownConfig, BsDropdownModule } from 'ngx-bootstrap/dropdown';
 * // NGX Datatable
 * import { NgxDatatableModule } from '@swimlane/ngx-datatable';
 *
 * &#64;NgModule({
 *   imports: [BsDropdownModule.forRoot(), NgxDatatableModule, TableModule,...],
 *   providers: [BsDropdownConfig]
 * })
 * export class AppModule(){}
 * </pre></code>
 *
 * Optional:
 * <code><pre>
 * import { NgxDataTableConfig, TableConfig, TableEvent } from 'patternfly-ng/table';
 * </pre></code>
 */

export class TableComponent extends TableBase implements AfterViewInit, DoCheck, OnInit {
  /**
   * An array of items to display for table columns
   */
   columns: any[];

  /**
   * The table config containing component properties
   */
   config: TableConfig;

  /**
   * The ngx-datatable config containing component properties
   */
   dataTableConfig: NgxDataTableConfig;

  /**
   * The name of the template used with expanding rows
   */
   expandRowTemplate: TemplateRef<any>;

  /**
   * The name of the template used with group headers
   */
   groupHeaderTemplate: TemplateRef<any>;

  /**
   * The ngx-datatable event emitted when a cell or row was focused via keyboard or mouse click
   */
   rows: any[];

  /**
   * The ngx-datatable event emitted when a cell or row was focused via keyboard or mouse click
   */
   onActivate = new EventEmitter();

  /**
   * The ngx-datatable event emitted when a row detail row was toggled
   *
   * Not applicable with pfng-table useExpandRows
   */
   onDetailToggle = new EventEmitter();

  /**
   * The ngx-datatable event emitted when a row detail row was toggled
   *
   * Not applicable with pfng-table paginationConfig
   */
   onPage = new EventEmitter();

  /**
   * The ngx-datatable event emitted when columns are re-ordered
   */
   onReorder = new EventEmitter();

  /**
   * The ngx-datatable event emitted when a column is resized
   */
   onResize = new EventEmitter();

  /**
   * The ngx-datatable event emitted when a cell or row was selected
   *
   * Not applicable with pfng-table showCheckbox
   */
   onSelect = new EventEmitter();

  /**
   * The ngx-datatable event emitted when body was scrolled (e.g., when scrollbarV is true)
   */
   onScroll = new EventEmitter();

  /**
   * The ngx-datatable event emitted when a column header is sorted
   */
   onSort = new EventEmitter();

  /**
   * The ngx-datatable event emitted when a context menu is invoked on the table
   */
   onTableContextMenu = new EventEmitter();

  /**
   * The event emitted when a row has been dragged
   */
  // @Output('onDrag') onDrag = new EventEmitter();

  /**
   * The event emitted when a row has been dropped
   */
   onDrop = new EventEmitter();

   private _datatable: DatatableComponent;
   private selectCellTemplate: TemplateRef<any>;
   private selectHeadTemplate: TemplateRef<any>;

  private _allRowsSelected: boolean = false;
  private _cols: any[];
  private _selectedRows: any[];
  private _showTable: boolean = true;

  private defaultConfig = {
    dragEnabled: false,
    hideClose: false,
    showCheckbox: false,
    styleClass: 'patternfly',
    useExpandRows: false
  } as TableConfig;

  private defaultDataTableConfig = {
    columnMode: 'force',
    cssClasses: {
      sortAscending: 'datatable-icon-up',
      sortDescending: 'datatable-icon-down',
      pagerLeftArrow: 'datatable-icon-left',
      pagerRightArrow: 'datatable-icon-right',
      pagerPrevious: 'datatable-icon-prev',
      pagerNext: 'datatable-icon-skip'
    },
    externalPaging: false,
    externalSorting: false,
    headerHeight: 50,
    messages: { emptyMessage: 'No records found' },
    offset: 0,
    reorderable: true,
    rowHeight: 'auto',
    rowIdentity: ((x: any) => x),
    scrollbarH: false,
    scrollbarV: false,
    sorts: [],
    sortType: 'multi'
  } as NgxDataTableConfig;

  private dragulaName = 'newBag';
  private prevConfig: TableConfig;
  private prevDataTableConfig: NgxDataTableConfig;
  private prevRows: any[];
  private rowsModel: any[];

  /**
   * The default constructor
   */
  constructor(private dragulaService: DragulaService) {
    super();
  }

  // Initialization

  /**
   * Setup component configuration upon view initialization
   */
  ngAfterViewInit(): void {
    // Reinitialize to include selection column cell/header templates
    this.setupSelectionCols();
  }

  /**
   *  Setup component configuration upon initialization
   */
  ngOnInit(): void {
    this.setupConfig();
    this.setupSelectionCols(); // Initialize here for selection column width
    this.setupDataTableConfig();
  }

  /**
   *  Check if the component config has changed
   */
  ngDoCheck(): void {
    // Do a deep compare on config
    if (!isEqual(this.config, this.prevConfig)) {
      // Skip pagination and toolbar changes
      if (!(this.hasPaginationChanged() || this.hasToolbarChanged())) {
        this.setupSelectionCols();
      }
      this.setupConfig();
    }
    if (!isEqual(this.dataTableConfig, this.prevDataTableConfig)) {
      this.setupDataTableConfig();
    }
    if (!isEqual(this.rows, this.prevRows)) {
      this.rowsModel = [...this.rows];
      this.initSelectedRows();
      this.initAllRowsSelected();

      // Disable toolbar actions
      if (this.config.toolbarConfig !== undefined) {
        this.config.toolbarConfig.disabled = !this.hasData;
      }

      // ngx-datatable recommends you force change detection -- issue #337
      if (this.prevRows === undefined || this.prevRows.length === 0) {
        setTimeout(() => {
          this.setupSelectionCols();
        }, 10);
      }
      this.prevRows = clone(this.rows); // lodash has issues deep cloning templates
    }
  }

  /**
   * Set up default config
   */
  protected setupConfig(): void {
    if (this.config !== undefined) {
      defaults(this.config, this.defaultConfig);
    } else {
      this.config = cloneDeep(this.defaultConfig);
    }
    // Disable toolbar actions
    if (this.config.toolbarConfig !== undefined && !this.hasData) {
      this.showTable = false;

      // Filter and sort don't fully disable without this timeout
      setTimeout(() => {
        this.config.toolbarConfig.disabled = !this.hasData;
        this.showTable = true;
      }, 10);
    }
    this.prevConfig = cloneDeep(this.config);
  }

  /**
   * Set up default ngx-datatable config
   */
  protected setupDataTableConfig(): void {
    if (this.dataTableConfig !== undefined) {
      defaults(this.dataTableConfig, this.defaultDataTableConfig);
    } else {
      this.dataTableConfig = cloneDeep(this.defaultDataTableConfig);
    }
    this.prevDataTableConfig = cloneDeep(this.dataTableConfig);
  }

  /**
   * Set up selection columns
   */
  protected setupSelectionCols(): void {
    let cellClass = '';
    if (this.config.dragEnabled === true
        && (this.config.useExpandRows !== true && this.config.showCheckbox !== true)) {
      cellClass = 'pfng-table-dnd-only';
    }

    // ngx-datatable requires width property to become visible
    let width = 0;
    if (this.config.showCheckbox === true && this.config.useExpandRows === true && this.config.dragEnabled === true) {
      width = 57;
    } else if (this.config.showCheckbox === true && this.config.useExpandRows === true) {
      width = 52;
    } else if (this.config.showCheckbox === true && this.config.dragEnabled === true) {
      width = 36;
    } else if (this.config.useExpandRows === true && this.config.dragEnabled === true) {
      width = 32;
    } else if (this.config.showCheckbox === true) {
      width = 34;
    } else if (this.config.useExpandRows === true) {
      width = 30;
    } else if (this.config.dragEnabled === true) {
      width = 10;
    }

    this._cols = [];
    if (width > 0) {
      this._cols.push({
        canAutoResize: false,
        cellClass: 'pfng-table-select ' + cellClass,
        cellTemplate: this.selectCellTemplate,
        headerClass: 'pfng-table-select ' + cellClass,
        headerTemplate: this.selectHeadTemplate,
        resizeable: false,
        sortable: false,
        width: width
      });
    }

    this.columns.forEach((col) => {
      this._cols.push(col);
    });
  }

  // Accessors

  /**
   * Returns a flag indicating whether all visible rows are selected
   *
   * @returns {boolean} True if all visible rows are selected
   */
  get allRowsSelected(): boolean {
    return (this.rows !== undefined && this.rows.length > 0) ? this._allRowsSelected : false;
  }

  /**
   * Sets a flag indicating whether all visible rows are selected
   *
   * @param {boolean} selected True if all visible rows are selected
   */
  set allRowsSelected(selected: boolean) {
    this._allRowsSelected = selected;
    if (this.rows !== undefined) {
      for (let i = 0; i < this.rows.length; i++) {
        this.rows[i].selected = (selected === true) ? true : false;
      }
    }
  }

  /**
   * Returns the columns used by the ngx-datatable component
   *
   * @returns {any[]} The ngx-datatable columns
   */
  protected get cols(): any[] {
    return this._cols;
  }

  /**
   * Returns the underlying ngx-datatable component
   *
   * @returns {DatatableComponent}
   */
  get datatable(): DatatableComponent {
    return this._datatable;
  }

  /**
   * Get the flag indicating table has data, including filtered rows
   *
   * @returns {boolean} True is the table has data
   */
  get hasData(): boolean {
    let hasRows = (this.rows !== undefined && this.rows.length > 0);
    let hasFilter = false;

    if (this.config.appliedFilters !== undefined) {
      hasFilter = (this.config.appliedFilters.length > 0);
    } else if (this.config.toolbarConfig !== undefined
        && this.config.toolbarConfig.filterConfig !== undefined
        && this.config.toolbarConfig.filterConfig.appliedFilters !== undefined) {
      hasFilter = (this.config.toolbarConfig.filterConfig.appliedFilters.length > 0);
    }
    return hasRows || hasFilter;
  }

  /**
   * Returns the currently selected rows
   *
   * @returns {any[]} The selected rows
   */
  get selectedRows(): any[] {
    return (this.dataTableConfig.selected !== undefined && this.config.showCheckbox !== true)
      ? this.dataTableConfig.selected : this._selectedRows;
  }

  /**
   * Sets the currently selected rows
   *
   * @param {any[]} selectedRows The selected rows
   */
  set selectedRows(selectedRows: any[]) {
    if (selectedRows !== undefined) {
      this._selectedRows = selectedRows;
      if (this.config.toolbarConfig !== undefined && this.config.toolbarConfig.filterConfig !== undefined) {
        this.config.toolbarConfig.filterConfig.selectedCount = this._selectedRows.length;
      }
    }
  }

  /**
   * Returns flag indicating table is visible
   *
   * @returns {boolean} True if table is visible
   */
  protected get showTable(): boolean {
    return this._showTable;
  }

  /**
   * Set the flag indicating table is visible
   *
   * @param {boolean} visible True if table is visible
   */
  protected set showTable(visible: boolean) {
    this._showTable = visible;
  }

  // Private

  /**
   * Helper to generate ngx-datatable activate event
   */
  private handleActivate($event: any): void {
    this.onActivate.emit($event);
  }

  /**
   * Helper to generate ngx-datatable detailToggle event
   */
  private handleDetailToggle($event: any): void {
    this.onDetailToggle.emit($event);
  }

  // Todo: Not implemented yet
  private handleDragulaDrag($event: any[]): void {
    // this.onDrag.emit($event);
  }

  /**
   * Helper to generate dragula drop event
   *
   * @param {any[]} $event
   */
  private handleDragulaDrop($event: any[]): void {
    // ngx-datatable recommends you force change detection
    this.showTable = false;
    this.rows = [...$event];

    setTimeout(() => {
      this.onDrop.emit($event);
      this.rowsModel = [...this.rows];
      this.showTable = true;
    }, 10);
  }

  /**
   * Helper to generate ngx-datatable page event
   */
  private handlePage($event: any): void {
    this.onPage.emit($event);
  }

  /**
   * Helper to generate ngx-datatable reorder event
   */
  private handleReorder($event: any): void {
    this.onReorder.emit($event);

    // Save new order for drag and drop changes
    let newCols = this.cols.slice();
    newCols.splice($event.prevValue, 1);
    newCols.splice($event.newValue, 0, $event.column);
    this._cols = newCols;
  }

  /**
   * Helper to generate ngx-datatable resize event
   */
  private handleResize($event: any): void {
    this.onResize.emit($event);
  }

  /**
   * Helper to generate ngx-datatable scroll event
   */
  private handleScroll($event: any): void {
    this.onScroll.emit($event);
  }

  /**
   * Helper to generate ngx-datatable select event
   */
  private handleSelect($event: any): void {
    this.onSelect.emit($event);
  }

  /**
   * Helper to generate ngx-datatable sort event
   */
  private handleSort($event: any): void {
    this.onSort.emit($event);
  }

  /**
   * Helper to generate ngx-datatable tableContextmenu event
   */
  private handleTableContextMenu($event: any): void {
    this.onTableContextMenu.emit($event);
  }

  /**
   * Helper to test if pagination config has changed
   *
   * @returns {boolean} True if pagination config has changed
   */
  private hasPaginationChanged(): boolean {
    let change = (this.config.paginationConfig !== undefined && this.prevConfig.paginationConfig !== undefined
      && !isEqual(this.config.paginationConfig, this.prevConfig.paginationConfig));
    return change;
  }

  /**
   * Helper to test if toolbar config has changed
   *
   * @returns {boolean} True if toolbar config has changed
   */
  private hasToolbarChanged(): boolean {
    let change = (this.config.toolbarConfig !== undefined && this.prevConfig.toolbarConfig !== undefined
      && !isEqual(this.config.toolbarConfig, this.prevConfig.toolbarConfig));
    return change;
  }

  /**
   * Helper to initialize de/select all control
   */
  private initAllRowsSelected(): void {
    this._allRowsSelected = (this.selectedRows !== undefined && this.selectedRows.length === this.rows.length);
  }

  /**
   * Helper to initialize selected rows
   */
  private initSelectedRows(): void {
    let selected = [];
    if (this.rows !== undefined) {
      for (let i = 0; i < this.rows.length; i++) {
        if (this.rows[i].selected) {
          selected.push(this.rows[i]);
        }
      }
    }
    this.selectedRows = selected;
  }

  /**
   * Helper to generate selection change event
   *
   * @param row The selected row
   */
  private selectionChange(row: any): void {
    this.initSelectedRows();
    this.initAllRowsSelected();
    this.onSelectionChange.emit({
      row: row,
      selectedRows: this.selectedRows
    } as TableEvent);
  }

  /**
   * Helper to generate selection change event when all rows are selected
   */
  private selectionsChange(): void {
    this.selectedRows = (this.allRowsSelected === true) ? this.rows : [];
    this.onSelectionChange.emit({
      selectedRows: this.selectedRows
    } as TableEvent);
  }

  /**
   * Helper to expand group
   *
   * @param group The group to expand
   */
  private toggleExpandGroup(group: any): void {
    this.datatable.groupHeader.toggleExpandGroup(group);
  }

  /**
   * Helper to expand row
   *
   * @param row The row to expand
   */
  private toggleExpandRow(row: any): void {
    if (this.datatable.rowDetail !== undefined) {
      this.datatable.rowDetail.toggleExpandRow(row);
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  encapsulation: ViewEncapsulation.None,
  selector: 'pfng-table',
  template: `<div class="pfng-table"><pfng-toolbar [config]="config.toolbarConfig" [actionTemplate]="actionTemplate" (onActionSelect)="handleAction($event)" (onFilterChange)="handleFilterChange($event)" (onFilterFieldSelect)="handleFilterFieldSelect($event)" (onFilterTypeAhead)="handleFilterTypeAhead($event)" (onSortChange)="handleSortChange($event)" (onViewSelect)="handleViewSelect($event)" *ngIf="config.toolbarConfig !== undefined"></pfng-toolbar><div *ngIf="hasData"><ngx-datatable #datatable [columns]="cols" [columnMode]="dataTableConfig.columnMode" [count]="dataTableConfig.count" [cssClasses]="dataTableConfig.cssClasses" [displayCheck]="dataTableConfig.displayCheck" [dragulaClassSelector]="'pfng-table-dnd-header'" [dragulaModel]="rowsModel" [dragulaName]="dragulaName" [externalPaging]="dataTableConfig.externalPaging" [externalSorting]="dataTableConfig.externalSorting" [footerHeight]="dataTableConfig.footerHeight" [groupExpansionDefault]="dataTableConfig.groupExpansionDefault" [groupRowsBy]="dataTableConfig.groupRowsBy" [headerHeight]="dataTableConfig.headerHeight" [messages]="dataTableConfig.messages" [ngClass]="config.styleClass" [limit]="dataTableConfig.limit" [loadingIndicator]="dataTableConfig.loadingIndicator" [offset]="dataTableConfig.offset" [reorderable]="dataTableConfig.reorderable" [rowClass]="dataTableConfig.rowClass" [rowHeight]="dataTableConfig.rowHeight" [rowIdentity]="dataTableConfig.rowIdentity" [rows]="rows" [scrollbarH]="dataTableConfig.scrollbarH" [scrollbarV]="dataTableConfig.scrollbarV" [selectAllRowsOnPage]="dataTableConfig.selectAllRowsOnPage" [selectCheck]="dataTableConfig.selectCheck" [selected]="selectedRows" [selectionType]="dataTableConfig.selectionType" [sorts]="dataTableConfig.sorts" [sortType]="dataTableConfig.sortType" [trackByProp]="dataTableConfig.trackByProp" [virtualization]="dataTableConfig.virtualization" (activate)="handleActivate($event)" (detailToggle)="handleDetailToggle($event)" (dragulaDrop)="handleDragulaDrop($event)" (dragulaDrag)="handleDragulaDrag($event)" (page)="handlePage($event)" (reorder)="handleReorder($event)" (resize)="handleResize($event)" (scroll)="handleScroll($event)" (select)="handleSelect($event)" (sort)="handleSort($event)" (tableContextmenu)="handleOnTableContextMenu($event)" *ngIf="showTable"><ng-template #selectHeadTemplate><span class="margin-left-5" *ngIf="config.dragEnabled === true"></span> <span class="margin-left-16" *ngIf="config.useExpandRows === true"><span class="pfng-list-expand-placeholder"></span> </span><input type="checkbox" value="allRowsSelected" title="{{(allRowsSelected) ? 'Deselect' : 'Select'}} All Rows" [disabled]="rows === undefined || rows.length === 0" [(ngModel)]="allRowsSelected" (ngModelChange)="selectionsChange()" *ngIf="config.showCheckbox === true"></ng-template><ng-template #selectCellTemplate let-row="row" let-expanded="expanded"><span class="pfng-table-dnd-container" *ngIf="config.dragEnabled === true"><span class="pfng-table-dnd-header"></span> </span><span [ngClass]="{'margin-left-5': config.dragEnabled === true}" *ngIf="config.useExpandRows === true"><span class="pfng-list-expand-placeholder" *ngIf="row.hideExpandToggle === true"></span> <span class="fa" [ngClass]="{'fa-angle-down': expanded, 'fa-angle-right margin-right-4': !expanded}" (click)="toggleExpandRow(row)" *ngIf="row.hideExpandToggle !== true"></span> </span><span [ngClass]="{'margin-left-5': config.dragEnabled === true || config.useExpandRows === true}" *ngIf="config.showCheckbox === true"><input type="checkbox" value="row.selected" title="{{(row.selected) ? 'Deselect' : 'Select'}} Row" [(ngModel)]="row.selected" (ngModelChange)="selectionChange(row)"></span></ng-template><ngx-datatable-group-header [rowHeight]="dataTableConfig.rowHeight" *ngIf="groupHeaderTemplate !== undefined"><ng-template let-group="group" let-expanded="expanded" ngx-datatable-group-header-template><span class="margin-5"><span class="fa" [ngClass]="{'fa-angle-down': expanded, 'fa-angle-right margin-right-4': !expanded}" (click)="toggleExpandGroup(group)"></span></span><ng-template [ngTemplateOutlet]="groupHeaderTemplate" [ngTemplateOutletContext]="{ group: group, expanded: expanded }"></ng-template></ng-template></ngx-datatable-group-header><ngx-datatable-row-detail [rowHeight]="auto" *ngIf="expandRowTemplate !== undefined"><ng-template let-row="row" let-expanded="expanded" ngx-datatable-row-detail-template><div class="pfng-table-expand-container" tabindex="0"><div class="pfng-table-expand-content" style="flex-grow: 1;"><div class="close" *ngIf="config.hideClose !== true"><span class="pficon pficon-close" (click)="toggleExpandRow(row)"></span></div><ng-template [ngTemplateOutlet]="expandRowTemplate" [ngTemplateOutletContext]="{ row: row, expanded: expanded }"></ng-template></div></div></ng-template></ngx-datatable-row-detail></ngx-datatable><pfng-pagination [config]="config.paginationConfig" (onPageNumberChange)="handlePageNumber($event)" (onPageSizeChange)="handlePageSize($event)" *ngIf="config.paginationConfig !== undefined"></pfng-pagination></div><pfng-empty-state [config]="config.emptyStateConfig" (onActionSelect)="handleAction($event)" *ngIf="!hasData"></pfng-empty-state></div>`
}, ] },
];
/** @nocollapse */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: DragulaService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'columns': [{ type: Input },],
'config': [{ type: Input },],
'dataTableConfig': [{ type: Input },],
'expandRowTemplate': [{ type: Input },],
'groupHeaderTemplate': [{ type: Input },],
'rows': [{ type: Input },],
'onActivate': [{ type: Output, args: ['onActivate', ] },],
'onDetailToggle': [{ type: Output, args: ['onDetailToggle', ] },],
'onPage': [{ type: Output, args: ['onPage', ] },],
'onReorder': [{ type: Output, args: ['onReorder', ] },],
'onResize': [{ type: Output, args: ['onResize', ] },],
'onSelect': [{ type: Output, args: ['onSelect', ] },],
'onScroll': [{ type: Output, args: ['onScroll', ] },],
'onSort': [{ type: Output, args: ['onSort', ] },],
'onTableContextMenu': [{ type: Output, args: ['onTableContextMenu', ] },],
'onDrop': [{ type: Output, args: ['onDrop', ] },],
'_datatable': [{ type: ViewChild, args: ['datatable', ] },],
'selectCellTemplate': [{ type: ViewChild, args: ['selectCellTemplate', ] },],
'selectHeadTemplate': [{ type: ViewChild, args: ['selectHeadTemplate', ] },],
};
}

interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
